/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper, UpIcon, DownIcon } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, ETimePeriod, formatNumber, formatToDatePeriod, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { getBudgetProgressColor, RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 15,
                flexFlow: 'row wrap',
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0 10px 10px 10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
                '& .sub': {
                    display: 'flex',
                    flexFlow: 'row wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                '& .status': {
                    marginLeft: '10px',
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'center',
                },
            },
            '& .increase': {
                color: 'green',
            },
            '& .decrease': {
                color: 'red',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            // maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        drilldownLine: {
            cursor: 'pointer',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum LastCountEnum {
    PREV_MONTH,
    MONTH,
    FINANCIAL_YEAR,
    PREV_FINANCIAL_YEAR,
}

interface FeesBilledWithBudgetInLastFinancialYearChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    last: LastCountEnum;
    months: string[];
    counts: number[];
    budgets: number[];
    currentYearTotal: number;
    currentTotal: number;
    leftPeriodString: string;
    rightPeriodString: string;
    budgetTotal: number;
    variance: number;
    budgetPercentage: number;
    yearBudget: number;
    yearVariance: number;
    yearPercentage: number;
    symbol: string;
}

export const FeesRecordedWithBudgetInFinancialYearChart: React.FC<FeesBilledWithBudgetInLastFinancialYearChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<TimeFeesData>(ChartData, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        last: LastCountEnum.MONTH,
        months: [],
        counts: [],
        budgets: [],
        currentYearTotal: 0,
        currentTotal: 0,
        leftPeriodString: formatToDatePeriod('monthToDate'),
        yearBudget: 0,
        yearVariance: 0,
        yearPercentage: 0,
        budgetTotal: 0,
        budgetPercentage: 0,
        variance: 0,
        rightPeriodString: formatToDatePeriod('yearToDate'),
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let months: string[] = [];
                let counts: number[] = [];
                let budgets: number[] = [];
                let currentTotal: number = 0;
                let currentYearTotal: number = 0;
                let leftPeriodString: string = '';
                let yearBudget: number = 0;
                let yearVariance: number = 0;
                let yearPercentage: number = 0;
                let budgetTotal: number = 0;
                let budgetPercentage: number = 0;
                let variance: number = 0;
                let rightPeriodString: string = '';
                if (!error && data) {
                    var lastMonth = data.statistics.time.feesRecordedWithBudgetYearToDate.lastMonth;
                    var previousMonth = data.statistics.time.feesRecordedWithBudgetYearToDate.previousMonth;
                    var lastYear = data.statistics.time.feesRecordedWithBudgetYearToDate.lastFinancialYear;
                    var prevYear = data.statistics.time.feesRecordedWithBudgetYearToDate.previousFinancialYear;

                    yearBudget = lastYear.summary.budget;
                    yearVariance = lastYear.summary.variance;
                    yearPercentage = lastYear.summary.percentage;
                    currentYearTotal = lastYear.summary.lastPeriodTotal;

                    if (state.last === LastCountEnum.MONTH) {
                        months = lastMonth.timesheetWithBudgetGrouping.map((s) => s.group);
                        counts = lastMonth.timesheetWithBudgetGrouping.map((s) => s.value);
                        budgets = lastMonth.timesheetWithBudgetGrouping.map((s) => s.budget);
                        currentTotal = lastMonth.summary.lastPeriodTotal;

                        budgetTotal = lastMonth.summary.budget;
                        variance = lastMonth.summary.variance;
                        budgetPercentage = lastMonth.summary.percentage;

                        leftPeriodString = formatToDatePeriod('monthToDate');
                        rightPeriodString = formatToDatePeriod('yearToDate');
                    } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                        months = lastYear.timesheetWithBudgetGrouping.map((s) => s.group);
                        counts = lastYear.timesheetWithBudgetGrouping.map((s) => s.value);
                        budgets = lastYear.timesheetWithBudgetGrouping.map((s) => s.budget);
                        currentTotal = lastYear.summary.lastPeriodTotal;

                        budgetTotal = lastYear.summary.budget;
                        variance = lastYear.summary.variance;
                        budgetPercentage = lastYear.summary.percentage;

                        leftPeriodString = formatToDatePeriod('yearToDate');
                        rightPeriodString = formatToDatePeriod('yearToDate');
                    } else if (state.last === LastCountEnum.PREV_MONTH) {
                        months = previousMonth.timesheetWithBudgetGrouping.map((s) => s.group);
                        counts = previousMonth.timesheetWithBudgetGrouping.map((s) => s.value);
                        budgets = previousMonth.timesheetWithBudgetGrouping.map((s) => s.budget);

                        currentTotal = previousMonth.summary.lastPeriodTotal;
                        budgetTotal = previousMonth.summary.budget;
                        variance = previousMonth.summary.variance;
                        budgetPercentage = previousMonth.summary.percentage;

                        if (previousMonth.financialYearToPreviousMonthSummary) {
                            currentYearTotal = previousMonth.financialYearToPreviousMonthSummary.lastPeriodTotal;
                            yearBudget = previousMonth.financialYearToPreviousMonthSummary.budget;
                            yearVariance = previousMonth.financialYearToPreviousMonthSummary.variance;
                            yearPercentage = previousMonth.financialYearToPreviousMonthSummary.percentage;
                        } else {
                            currentYearTotal = 0;
                            yearBudget = 0;
                            yearVariance = 0;
                            yearPercentage = 0;
                        }

                        leftPeriodString = formatToDatePeriod('previousMonth');
                        rightPeriodString = formatToDatePeriod('previousMonthYearToDate');
                    } else {
                        months = prevYear.timesheetWithBudgetGrouping.map((s) => s.group);
                        counts = prevYear.timesheetWithBudgetGrouping.map((s) => s.value);
                        budgets = prevYear.timesheetWithBudgetGrouping.map((s) => s.budget);
                        currentTotal = prevYear.summary.lastPeriodTotal;

                        budgetTotal = prevYear.summary.budget;
                        variance = prevYear.summary.variance;
                        budgetPercentage = prevYear.summary.percentage;

                        leftPeriodString = formatToDatePeriod('previousYearToDate');
                        rightPeriodString = formatToDatePeriod('yearToDate');
                    }
                }

                return {
                    ...prevState,
                    months,
                    counts,
                    budgets,
                    currentTotal,
                    currentYearTotal,
                    leftPeriodString,
                    yearBudget,
                    yearPercentage,
                    yearVariance,
                    budgetTotal,
                    variance,
                    budgetPercentage,
                    rightPeriodString,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading, error, data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('IsTimesheetFeesWithBudgetInFinancialYearChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isFinancialYear: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            var defaultView = 'Fees Produced MTD/YTD';
            let timePeriod = ETimePeriod.THIS_MONTH;
            if (!isFinancialYear) {
                if (state.last === LastCountEnum.MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.THIS_MONTH;

                } else if (state.last === LastCountEnum.PREV_MONTH) {
                    searchCriterias.push({ property: 'Month', operator: '', comparison: '', period: 'prev' });
                    defaultView = 'Fees Produced PM/PYTD';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;

                } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' });
                    timePeriod = ETimePeriod.FINANCIAL_YEAR;

                } else if (state.last === LastCountEnum.PREV_FINANCIAL_YEAR) {
                    searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'prev' });
                    timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                    defaultView = 'Fees Produced PM/PYTD';
                }
            } else {
                var period = 'curr';
                timePeriod = ETimePeriod.FINANCIAL_YEAR;
                if (state.last === LastCountEnum.PREV_MONTH) {
                    period = 'prevMonth';
                    timePeriod = ETimePeriod.PREVIOUS_MONTH;
                    defaultView = 'Fees Produced PM/PYTD';
                }
                searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: period });
            }
            // . e.g. - Apr 22, Apr 23, etc

            props.getSelectedItem(
                searchCriterias,
                false,
                `Drilldown from Fees Recorded vs Budget for ${timePeriod}`,
                DrillDownType.staffBillingSummary,
                defaultView,
            );
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.budgets, state.counts) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<MonetizationOnOutlinedIcon />} title="Fees Recorded vs Budget" />

                <div className="colWrapper">
                    <div className="col">
                        <div className="title">{state.leftPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.budgetPercentage))}%`, backgroundColor: getBudgetProgressColor(state.budgetPercentage) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(false)}>
                            {`Budget ${state.symbol}${formatNumber(state.budgetTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(false)}>
                                {`${formatNumber(state.budgetPercentage)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetTotal < state.currentTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(false)} title="Variance">
                                {state.budgetTotal < state.currentTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.variance)}`}
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">{state.rightPeriodString}</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentYearTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.yearPercentage))}%`, backgroundColor: getBudgetProgressColor(state.yearPercentage) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(true)}>
                            {`Budget ${state.symbol}${formatNumber(state.yearBudget)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(true)}>
                                {`${formatNumber(state.yearPercentage)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.yearBudget < state.currentYearTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(true)} title="Variance">
                                {state.yearBudget < state.currentYearTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.yearVariance)}`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.months[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            // var d = new Date();
                            // var year = d.getFullYear();
                            let timePeriod = ETimePeriod.THIS_MONTH;

                            if (state.last === LastCountEnum.MONTH) {
                                searchCriterias.push(
                                    { property: 'DateShortString', operator: 'eq', comparison: item }, //
                                );
                                timePeriod = ETimePeriod.THIS_MONTH;
            
                            } else if (state.last === LastCountEnum.PREV_MONTH) {
                                searchCriterias.push(
                                    { property: 'DateShortString', operator: 'eq', comparison: item }, //
                                );
                                timePeriod = ETimePeriod.PREVIOUS_MONTH;
            
                            } else if (state.last === LastCountEnum.FINANCIAL_YEAR) {
                                searchCriterias.push({ property: 'MonthStart', operator: 'gteq', comparison: item }, { property: 'MonthEnd', operator: 'lteq', comparison: item });
                                timePeriod = ETimePeriod.FINANCIAL_YEAR;
            
                            } else if (state.last === LastCountEnum.PREV_FINANCIAL_YEAR) {
                                searchCriterias.push({ property: 'MonthStart', operator: 'gteq', comparison: item }, { property: 'MonthEnd', operator: 'lteq', comparison: item });
                                timePeriod = ETimePeriod.PREVIOUS_FINANCIAL_YEAR;
                            }

                            // . e.g. - Apr 22, Apr 23, etc
                            searchCriterias.push({ property: 'Amount', operator: 'ne', comparison: '0' });

                            const title = `Drilldown from Fees Recorded vs Budget for ${timePeriod}`;

                            return props.getSelectedItem(
                                searchCriterias, 
                                false, 
                                `${title} - ${item}`,
                                DrillDownType.timesheetSummary, 'Timesheet Lines'
                            );
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Budget',
                                backgroundColor: RV_CHART_COLORS.amber,
                                borderColor: RV_CHART_COLORS.amber,
                                borderWidth: 3,
                                hoverBackgroundColor: RV_CHART_COLORS.amber,
                                hoverBorderColor: RV_CHART_COLORS.amber,
                                tension: 0.35,
                                pointRadius: 0,
                                data: state.budgets,
                                type: 'line',
                                pointStyle: 'line',
                            },
                            {
                                fill: false,
                                label: 'Fees Recorded',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                                type: 'bar',
                            },
                            // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.budgets, ...state.counts]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.budgets, ...state.counts]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.budgets, state.counts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={LastCountEnum.PREV_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                    <FormControlLabel value={LastCountEnum.FINANCIAL_YEAR} control={<Radio color="primary" />} label="Financial Year" />
                    <FormControlLabel value={LastCountEnum.PREV_FINANCIAL_YEAR} control={<Radio color="primary" />} label="Previous Financial Year" />
                </RadioGroup>
            </FormControl>
            {/* <div className={classes.drilldownLine}>Drilldown to timesheet lines</div>                                       */}
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface TimeFeesData {
    statistics: Statistics;
}

export interface Statistics {
    time: Time;
}

export interface Time {
    feesRecordedWithBudgetYearToDate: FeesRecordedWithBudgetYearToDate;
}

export interface FeesRecordedWithBudgetYearToDate {
    lastMonth: Last;
    lastFinancialYear: Last;
    previousFinancialYear: Previous;
    previousMonth: Previous;
}

export interface Last {
    summary: Summary;
    timesheetWithBudgetGrouping: TimesheetWithBudgetGrouping[];
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
    budget: number;
    variance: number;
    percentage: number;
}

export interface TimesheetWithBudgetGrouping {
    value: number;
    group: string;
    budget: number;
}

export interface Previous {
    summary: Summary;
    financialYearToPreviousMonthSummary: Summary | null;
    timesheetWithBudgetGrouping: TimesheetWithBudgetGrouping[];
}
const ChartData = gql`
    query FeesRecordedWithBudgetYearToDateChartData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            time {
                feesRecordedWithBudgetYearToDate(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    lastMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            variance
                            percentage
                        }
                        timesheetWithBudgetGrouping {
                            value
                            group
                            budget
                        }
                    }
                    lastFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            variance
                            percentage
                        }
                        timesheetWithBudgetGrouping {
                            value
                            group
                            budget
                        }
                    }
                    previousFinancialYear {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            variance
                            percentage
                        }
                        timesheetWithBudgetGrouping {
                            value
                            group
                            budget
                        }
                    }
                    previousMonth {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            variance
                            percentage
                        }
                        financialYearToPreviousMonthSummary {
                            lastPeriodTotal
                            previousPeriodTotal
                            budget
                            variance
                            percentage
                        }
                        timesheetWithBudgetGrouping {
                            value
                            group
                            budget
                        }
                    }
                }
            }
        }
    }
`;
